/*
 * Copyright Mimic Networks, Inc. 2024.
 */

import { useNavigate } from 'react-router-dom';

import { PaginatedUsersResponse, User, UserFilters, UserResponse } from '@/client';
import { PaginatedTable } from '@/components/PaginatedTable';
import { useMessage } from '@/hooks/message';
import { useUpdateUserMutation } from '@/hooks/mutation/useUpdateUserMutation';
import { useMimicTranslation } from '@/hooks/useMimicTranslation';
import { useActiveUsersColumns } from '@/pages/Users/useActiveUsersColumns';
import { useRemovedUsersColumns } from '@/pages/Users/useRemovedUsersColumns';
import { Role } from '@/pages/Users/UserTable';
import { Container } from '@/primitives/Container';
import { useMgmtPlaneStateStore } from '@/state/mgmtPlaneStore';
import { UpdateParams } from '@/utils/params';

export type UsersListProps = {
  usersData: PaginatedUsersResponse;
  filters: UserFilters;
  onParamsChange: UpdateParams<User, UserFilters>;
};

export function UsersList({ usersData, filters, onParamsChange }: UsersListProps) {
  const { t } = useMimicTranslation('users');
  const navigate = useNavigate();
  const currentUser = useMgmtPlaneStateStore((state) => state.userProfile);
  const tenantID = useMgmtPlaneStateStore((state) => state.selectedTenantID);
  const [message] = useMessage();

  const { mutate: updateUserMutation } = useUpdateUserMutation({
    onError: (_, updateUserDto) => {
      const displayName = `${updateUserDto.firstName} ${updateUserDto.lastName}`;
      message.error(t('feedback.errorOnUpdate', { user: `${displayName}` }));
    },
    onSuccess: ({ data: userUpdated }: UserResponse) => {
      message.success(t('feedback.userUpdated', { user: `${userUpdated.displayName}` }));
    },
  });

  const toggleSubtenantOnly = (user: User) => {
    updateUserMutation({ ...user, requestBody: { subtenantOnly: !user.subtenantOnly } });
  };

  const updateUserRole = (user: User, role: Role) => {
    updateUserMutation({ ...user, requestBody: { role } });
  };

  const updateUserRemoved = (user: User, removed: boolean) => {
    updateUserMutation({ ...user, requestBody: { removed } });
  };

  const openUserSubtenantsModal = (user: User) => {
    navigate(`/tenants/${tenantID}/users/${user.id}/subtenants`);
  };

  const navigateToUserDetails = (user: User) => {
    navigate(`/tenants/${tenantID}/users/${user.id}`);
  };

  const removedUserColumns = useRemovedUsersColumns(currentUser!, updateUserRemoved);
  const activeColumns = useActiveUsersColumns(
    currentUser!,
    updateUserRemoved,
    updateUserRole,
    openUserSubtenantsModal,
    navigateToUserDetails,
    toggleSubtenantOnly,
  );

  const columns = filters.removed ? removedUserColumns : activeColumns;

  return (
    <Container>
      <PaginatedTable
        dark={filters.removed}
        data-testid="user-table"
        rowKey={(tag) => tag.id}
        data={usersData}
        columns={columns}
        onParamsChange={onParamsChange}
      />
    </Container>
  );
}
